// src/widgets/time-widget/useTimeWidgetData.ts

import { useEffect, useMemo, useState } from 'react'
import type {
  TimeWidgetResult,
  PeriodSelection,
  Segment,
  ServiceType,
  Recipient,
} from './timeWidget.types'
import {
  bucketLabel,
  bucketKey,
  distributeHoursEvenly,
  enumerateBucketStarts,
  getPeriodRange,
  makeServiceType,
} from './timeWidget.utils'

import type { TimeframeKey } from '@/ui/components/utils/worktimeTransforms'
import { getActivitiesInRange } from '@/api/activity.api'
import { getOrdersByYear, getOrdersByYearCached, type OrderDTO } from '@/api/ordersTimeWidget.api'
import { getBanks, getBankById, getBankByIdCached } from '@/api/settings.api'
import { getServices } from '@/api/settings.api'

type ActivityLike = {
  date: Date | string
  timeQuantity: number
  type: string
  bankId: number
  customerId: number
}

const UNION_ID = 1

const HOURS_PER_UNIT: Record<ServiceType, number> = {
  TOJ: 2.5,
  CONSULTING: 1,
  TRAINING: 8,
  PILOT: 1,
}

const hoursToUnits = (st: ServiceType, hours: number) => {
  const hpu = HOURS_PER_UNIT[st] ?? 1
  return hpu > 0 ? hours / hpu : hours
}

function round1(n: number) {
  return Math.round(n * 10) / 10
}

function emptyKPIs() {
  return {
    overall: { worked: 0, ordered: 0, half: 0, eighty: 0, unit: 'unit' as const },
    toj: { worked: 0, ordered: 0, unit: 'unit' as const },
    consulting: { worked: 0, ordered: 0, unit: 'unit' as const },
    training: { worked: 0, ordered: 0, unit: 'unit' as const },
    pilot: { worked: 0, ordered: 0, unit: 'h' as const },
  }
}

export function useTimeWidgetData(args: {
  period: PeriodSelection
  timeframe: TimeframeKey
  selectedRecipientIds: string[]
  segment: Segment
}) {
  const { period, timeframe, selectedRecipientIds, segment } = args
  const fetchSpec = useMemo(() => {
    const { from: pFrom, to: pTo } = getPeriodRange(period, timeframe)

    const yFrom = pFrom.getFullYear()
    const yTo = pTo.getFullYear()

    const years: number[] = []
    for (let y = yFrom; y <= yTo; y++) years.push(y)

    return {
      key: `YEARS:${years.join(',')}`,
      years,
      from: new Date(yFrom, 0, 1, 0, 0, 0, 0),
      to: new Date(yTo, 11, 31, 23, 59, 59, 999),
    }
  }, [period, timeframe])

  const fetchKey = fetchSpec.key

  const fmtDay = (iso: string) => {
    const d = new Date(iso)
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    return `${dd}.${mm}`
  }

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()
  const [result, setResult] = useState<TimeWidgetResult>(() => {
    const r = getPeriodRange(period, timeframe)
    return {
      loading: true,
      periodLabel: r.label,
      granularity: r.granularity,
      recipients: [],
      kpis: emptyKPIs(),
      chart: {
        keys: [],
        labels: [],
        worked: [],
        ordered: [],
        workedByType: { TOJ: [], CONSULTING: [], TRAINING: [], PILOT: [] },
        orderedByType: { TOJ: [], CONSULTING: [], TRAINING: [], PILOT: [] },
      },
    }
  })

  // add this state to cache raw fetched data for the current period
  const [raw, setRaw] = useState<null | {
    from: Date
    to: Date
    label: string
    granularity: ReturnType<typeof getPeriodRange>['granularity']
    recipientNameById: Map<string, string>
    activities: ActivityLike[]
    approvedOrders: OrderDTO[]
    recipients: Recipient[] // the card list
    hoursPerUnitByType: Record<ServiceType, number>
  }>(null)

  useEffect(() => {
    let alive = true

    ;(async () => {
      setLoading(true)
      setError(undefined)

      try {
        const from = fetchSpec.from
        const to = fetchSpec.to

        const { label, granularity } = getPeriodRange(period, timeframe)
        const banksRes = await getBanks()
        const banks = banksRes.items ?? []

        const unionBank = banks.find(
          (b: any) =>
            String(b?.id) === String(UNION_ID) ||
            String(b?.name ?? '')
              .toLowerCase()
              .includes('union')
        )

        let unionCustomers: { id: number; name: string }[] = []

        if (unionBank?.id) {
          const unionDetails: any = await getBankByIdCached(unionBank.id)

          const associated =
            unionDetails?.associatedEntities ?? unionDetails?.item?.associatedEntities ?? []

          unionCustomers = associated.map((e: any) => ({
            id: Number(e.id),
            name: String(e.name ?? e.title ?? `Kunde ${e.id}`),
          }))
        }

        const recipientNameById = new Map<string, string>()
        for (const b of banks) {
          recipientNameById.set(String(b.id), String((b as any).name ?? `Bank ${b.id}`))
        }

        for (const c of unionCustomers) {
          recipientNameById.set(String(c.id), c.name)
        }

        // Build hours->unit conversion map (fallback 1h = 1 unit)
        const servicesRes = await getServices()
        const services = (servicesRes as any)?.items ?? servicesRes ?? []

        const hoursPerUnitByType: Record<ServiceType, number> = {
          TOJ: 1,
          CONSULTING: 1,
          TRAINING: 1,
          PILOT: 1,
        }

        // best-effort mapping - adjust field names if your API differs
        for (const s of services as any[]) {
          const name = String(s?.name ?? s?.title ?? '').toUpperCase()
          const hpu =
            Number(s?.hoursPerUnit ?? s?.unitHours ?? s?.hours_per_unit ?? s?.unit_size ?? 1) || 1

          if (name.includes('TOJ')) hoursPerUnitByType.TOJ = hpu
          else if (name.includes('BERAT')) hoursPerUnitByType.CONSULTING = hpu
          else if (name.includes('SCHUL')) hoursPerUnitByType.TRAINING = hpu
        }

        // 2) worked activities
        const activitiesRes = await getActivitiesInRange(from, to)
        const activities: ActivityLike[] = (activitiesRes.items ?? []) as any[]
        const allOrders: OrderDTO[] = []

        for (const y of fetchSpec.years) {
          const o = await getOrdersByYearCached(y)
          allOrders.push(...o)
        }

        const approvedOrders = allOrders.filter(o => String(o.status).toUpperCase() === 'APPROVED')
        const recipientsSet = new Map<string, Recipient>()
        const orderedByBucketByType = new Map<string, Record<ServiceType, number>>()
        const addOrdered = (bKey: string, st: ServiceType, hours: number) => {
          const cur = orderedByBucketByType.get(bKey) ?? {
            TOJ: 0,
            CONSULTING: 0,
            TRAINING: 0,
            PILOT: 0,
          }
          cur[st] += hours
          orderedByBucketByType.set(bKey, cur)
        }

        for (const a of activities) {
          if (Number(a.bankId) !== UNION_ID) continue
          const rid = String(a.customerId)
          recipientsSet.set(rid, {
            id: rid,
            name: recipientNameById.get(rid) ?? `Empfänger ${rid}`,
          })
        }

        for (const o of approvedOrders) {
          // ignore external
          if (Number(o.bankId) !== UNION_ID) continue

          // Union recipient MUST be customerId
          if (o.customerId == null) continue
          const rid = String(o.customerId)

          const orderFrom = new Date(o.startTime)
          const orderTo = new Date(o.endTime)

          for (const b of o.buckets ?? []) {
            const st = b.serviceType as ServiceType
            const plannedHours = Number(b.plannedHours ?? 0)
            if (!plannedHours) continue

            const plannedUnits = hoursToUnits(st, plannedHours)

            const dist = distributeHoursEvenly({
              totalHours: plannedUnits,
              from: orderFrom,
              to: orderTo,
              periodFrom: from,
              periodTo: to,
              granularity,
            })

            for (const [key, v] of dist.entries()) addOrdered(key, st, v)
          }
        }

        // ALSO include recipients that appear only in approved orders
        for (const o of approvedOrders) {
          if (Number(o.bankId) === UNION_ID) {
            if (o.customerId == null) continue
            const rid = String(o.customerId)
            recipientsSet.set(rid, {
              id: rid,
              name: recipientNameById.get(rid) ?? `Empfänger ${rid}`,
            })
          } else {
            // External: recipient is the bankId
            const rid = String(o.bankId)
            recipientsSet.set(rid, {
              id: rid,
              name: recipientNameById.get(rid) ?? `Bank ${rid}`,
            })
          }
        }

        const recipients = Array.from(recipientsSet.values()).sort((a, b) =>
          a.name.localeCompare(b.name)
        )

        console.log('selectedRecipientIds', selectedRecipientIds)
        console.log(
          'recipient ids available',
          recipients.map(r => r.id)
        )
        console.log(
          'first approved order rid example',
          approvedOrders.slice(0, 3).map(o => ({
            bankId: o.bankId,
            customerId: o.customerId,
            rid: String(Number(o.bankId) === UNION_ID ? (o.customerId ?? o.bankId) : o.bankId),
          }))
        )

        setRaw({
          from,
          to,
          label,
          granularity,
          recipientNameById,
          activities,
          approvedOrders,
          recipients,
          hoursPerUnitByType,
        })

        if (!alive) return
        setRaw({
          from,
          to,
          label,
          granularity,
          recipientNameById,
          activities,
          approvedOrders,
          recipients,
          hoursPerUnitByType,
        })
        setLoading(false)
      } catch (e: any) {
        if (!alive) return
        setError(e?.message ?? 'Unknown error')
        setLoading(false)
      }
    })()

    return () => {
      alive = false
    }
  }, [fetchKey])

  useEffect(() => {
    if (!raw) return

    const { activities, approvedOrders, recipients } = raw
    const {
      from: periodFrom,
      to: periodTo,
      label,
      granularity,
    } = getPeriodRange(period, timeframe)

    const selectedSet = new Set((selectedRecipientIds ?? []).map(String))
    const recipientAllowed = (rid: string) => selectedSet.has(String(rid))

    // ---------- aggregate WORKED ----------
    const workedByBucketByType = new Map<string, Record<ServiceType, number>>()
    const addWorked = (bKey: string, st: ServiceType, hours: number) => {
      const cur = workedByBucketByType.get(bKey) ?? {
        TOJ: 0,
        CONSULTING: 0,
        TRAINING: 0,
        PILOT: 0,
      }
      cur[st] += hours
      workedByBucketByType.set(bKey, cur)
    }

    for (const a of activities) {
      if (Number(a.bankId) !== UNION_ID) continue
      const rid = String(a.customerId)
      if (!recipientAllowed(rid)) continue

      const st = makeServiceType(a.type)
      if (!st) continue

      const d = a.date instanceof Date ? a.date : new Date(a.date)
      if (d < periodFrom || d > periodTo) continue
      const bKey = bucketKey(d, granularity)
      addWorked(bKey, st, Number(a.timeQuantity ?? 0))
    }

    // ---------- aggregate ORDERED ----------
    const orderedByBucketByType = new Map<string, Record<ServiceType, number>>()
    const addOrdered = (bKey: string, st: ServiceType, hours: number) => {
      const cur = orderedByBucketByType.get(bKey) ?? {
        TOJ: 0,
        CONSULTING: 0,
        TRAINING: 0,
        PILOT: 0,
      }
      cur[st] += hours
      orderedByBucketByType.set(bKey, cur)
    }

    for (const o of approvedOrders) {
      if (Number(o.bankId) !== UNION_ID) continue
      if (o.customerId == null) continue

      const rid = String(o.customerId)
      if (!recipientAllowed(rid)) continue

      const orderFrom = new Date(o.startTime)
      const orderTo = new Date(o.endTime)
      for (const b of o.buckets ?? []) {
        const st = b.serviceType as ServiceType
        const plannedHours = Number(b.plannedHours ?? 0)
        if (!plannedHours) continue

        const plannedUnits = hoursToUnits(st, plannedHours)

        const dist = distributeHoursEvenly({
          totalHours: plannedUnits,
          from: orderFrom,
          to: orderTo,
          periodFrom: periodFrom,
          periodTo: periodTo,
          granularity,
        })

        for (const [key, v] of dist.entries()) addOrdered(key, st, v)
      }
    }

    // ---------- build axis ----------
    const allKeysSet = new Set<string>([
      ...workedByBucketByType.keys(),
      ...orderedByBucketByType.keys(),
    ])
    const allKeys = Array.from(allKeysSet).sort()

    const keys =
      (period.kind === 'YTD' && granularity === 'month') ||
      (timeframe === 'Jahr' && granularity === 'month') ||
      (period.kind === 'YEAR' && granularity === 'month')
        ? enumerateBucketStarts(periodFrom, periodTo, 'month').map(d => bucketKey(d, 'month'))
        : timeframe === 'Woche' && granularity === 'day'
          ? enumerateBucketStarts(periodFrom, periodTo, 'day').map(d => bucketKey(d, 'day'))
          : timeframe === 'Monat' && granularity === 'day'
            ? enumerateBucketStarts(periodFrom, periodTo, 'day').map(d => bucketKey(d, 'day'))
            : timeframe === 'Quartal' && granularity === 'month'
              ? enumerateBucketStarts(periodFrom, periodTo, 'month').map(d =>
                  bucketKey(d, 'month')
                )
              : timeframe === 'Spezifisch' && granularity === 'day'
                ? enumerateBucketStarts(periodFrom, periodTo, 'day').map(d => bucketKey(d, 'day'))
                : timeframe === 'Spezifisch' && granularity === 'week'
                  ? enumerateBucketStarts(periodFrom, periodTo, 'week').map(d =>
                      bucketKey(d, 'week')
                    )
                  : timeframe === 'Spezifisch' && granularity === 'month'
                    ? enumerateBucketStarts(periodFrom, periodTo, 'month').map(d =>
                        bucketKey(d, 'month')
                      )
                    : allKeys

    // ---------- KPIs ----------
    const sumType = (map: Map<string, Record<ServiceType, number>>, t: ServiceType) => {
      let s = 0
      for (const v of map.values()) s += v[t] ?? 0
      return s
    }

    const workedTOJ = sumType(workedByBucketByType, 'TOJ')
    const workedCONS = sumType(workedByBucketByType, 'CONSULTING')
    const workedTR = sumType(workedByBucketByType, 'TRAINING')
    const workedPILOT = sumType(workedByBucketByType, 'PILOT')
    const workedAll = workedTOJ + workedCONS + workedTR + workedPILOT

    const orderedTOJ = sumType(orderedByBucketByType, 'TOJ')
    const orderedCONS = sumType(orderedByBucketByType, 'CONSULTING')
    const orderedTR = sumType(orderedByBucketByType, 'TRAINING')
    const orderedPILOT = sumType(orderedByBucketByType, 'PILOT')
    const orderedAll = orderedTOJ + orderedCONS + orderedTR + orderedPILOT

    const hoursPerUnit = raw.hoursPerUnitByType
    const toUnits = (hours: number, st: ServiceType) => {
      const denom = Number(hoursPerUnit?.[st] ?? 1)
      return denom > 0 ? hours / denom : hours
    }

    const workedTOJ_u = toUnits(workedTOJ, 'TOJ')
    const workedCONS_u = toUnits(workedCONS, 'CONSULTING')
    const workedTR_u = toUnits(workedTR, 'TRAINING')
    const workedPILOT_u = toUnits(workedPILOT, 'PILOT')
    const workedAll_u = workedTOJ_u + workedCONS_u + workedTR_u + workedPILOT_u

    const orderedTOJ_u = toUnits(orderedTOJ, 'TOJ')
    const orderedCONS_u = toUnits(orderedCONS, 'CONSULTING')
    const orderedTR_u = toUnits(orderedTR, 'TRAINING')
    const orderedPILOT_u = toUnits(orderedPILOT, 'PILOT')
    const orderedAll_u = orderedTOJ_u + orderedCONS_u + orderedTR_u + orderedPILOT_u

    const kpis = {
      overall: {
        worked: round1(workedAll_u),
        ordered: round1(orderedAll_u),
        half: round1(orderedAll_u * 0.5),
        eighty: round1(orderedAll_u * 0.8),
        unit: 'unit' as const,
      },
      toj: { worked: round1(workedTOJ_u), ordered: round1(orderedTOJ_u), unit: 'unit' as const },
      consulting: {
        worked: round1(workedCONS_u),
        ordered: round1(orderedCONS_u),
        unit: 'unit' as const,
      },
      training: {
        worked: round1(workedTR_u),
        ordered: round1(orderedTR_u),
        unit: 'unit' as const,
      },
      pilot: {
        worked: round1(workedPILOT_u),
        ordered: round1(orderedPILOT_u),
        unit: 'h' as const,
      },
    }

    // ---------- chart ----------
    const pick = (rec: Record<ServiceType, number> | undefined, seg: Segment): number => {
      if (!rec) return 0
      if (seg === 'OVERVIEW')
        return (rec.TOJ ?? 0) + (rec.CONSULTING ?? 0) + (rec.TRAINING ?? 0) + (rec.PILOT ?? 0)
      return rec[seg] ?? 0
    }

    const dow = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
    const fmtWeekDay = (iso: string) => {
      const d = new Date(iso) // iso is YYYY-MM-DD
      const weekday = (d.getDay() + 6) % 7
      const name = dow[weekday]
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      return `${name} ${dd}.${mm}`
    }

    const monthNames = [
      'Jan',
      'Feb',
      'Mär',
      'Apr',
      'Mai',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Okt',
      'Nov',
      'Dez',
    ]

    const labels = keys.map(k => {
      if (period.kind === 'YTD' && granularity === 'month') {
        const mm = Number(k.slice(5, 7)) // "YYYY-MM" -> MM
        return monthNames[mm - 1] ?? k
      }
      if (timeframe === 'Woche' && granularity === 'day') {
        return fmtWeekDay(k) // k is YYYY-MM-DD
      }

      if (timeframe === 'Monat' && granularity === 'day') return fmtDay(k)
      if (timeframe === 'Quartal' && granularity === 'month') {
        const mm = Number(k.slice(5, 7))
        return monthNames[mm - 1] ?? k
      }

      return bucketLabel(k, granularity, period.kind === 'YEAR' ? period.year : undefined)
    })

    const workedSeries = keys.map(k => pick(workedByBucketByType.get(k), segment))
    const orderedSeries = keys.map(k => pick(orderedByBucketByType.get(k), segment))

    const workedByType: Record<ServiceType, number[]> = {
      TOJ: keys.map(k => workedByBucketByType.get(k)?.TOJ ?? 0),
      CONSULTING: keys.map(k => workedByBucketByType.get(k)?.CONSULTING ?? 0),
      TRAINING: keys.map(k => workedByBucketByType.get(k)?.TRAINING ?? 0),
      PILOT: keys.map(k => workedByBucketByType.get(k)?.PILOT ?? 0),
    }

    const orderedByType: Record<ServiceType, number[]> = {
      TOJ: keys.map(k => orderedByBucketByType.get(k)?.TOJ ?? 0),
      CONSULTING: keys.map(k => orderedByBucketByType.get(k)?.CONSULTING ?? 0),
      TRAINING: keys.map(k => orderedByBucketByType.get(k)?.TRAINING ?? 0),
      PILOT: keys.map(k => orderedByBucketByType.get(k)?.PILOT ?? 0),
    }

    // ---------- empty state ----------
    let emptyState: TimeWidgetResult['emptyState'] | undefined

    // 1) no approved orders at all (global, not filtered by recipient)
    const noApprovedOrders = (raw.approvedOrders?.length ?? 0) === 0

    // 2) no recipient selected
    const noRecipientSelected = (selectedRecipientIds?.length ?? 0) === 0

    // 3) no activities AFTER filters (recipient + period)
    const noActivitiesAfterFilters = workedAll === 0

    if (noApprovedOrders) {
      emptyState = {
        kind: 'NO_DATA_IN_PERIOD',
        message:
          'Es liegen keine genehmigten Bestellungen vor.\nBitte erstellen Sie zuerst eine Bestellung.',
      }
    } else if (noRecipientSelected) {
      emptyState = {
        kind: 'NO_DATA_IN_PERIOD',
        message: 'Kein Leistungsempfänger ausgewählt.',
      }
    } else if (noActivitiesAfterFilters) {
      emptyState = {
        kind: 'NO_DATA_IN_PERIOD',
        message:
          'Es wurden keine Aktivitäten aufgezeichnet.\nBitte fügen Sie zuerst eine Aktivität hinzu.',
      }
    }

    setResult({
      loading: false,
      periodLabel: label,
      granularity,
      recipients,
      kpis,
      chart: {
        keys,
        labels,
        worked: workedSeries.map(round1),
        ordered: orderedSeries.map(round1),
        workedByType: {
          TOJ: workedByType.TOJ.map(round1),
          CONSULTING: workedByType.CONSULTING.map(round1),
          TRAINING: workedByType.TRAINING.map(round1),
          PILOT: workedByType.PILOT.map(round1),
        },
        orderedByType: {
          TOJ: orderedByType.TOJ.map(round1),
          CONSULTING: orderedByType.CONSULTING.map(round1),
          TRAINING: orderedByType.TRAINING.map(round1),
          PILOT: orderedByType.PILOT.map(round1),
        },
      },
      emptyState,
    })
  }, [raw, timeframe, selectedRecipientIds.join('|'), segment, period])

  return useMemo(
    () => ({
      ...result,
      loading,
      error,
    }),
    [result, loading, error]
  )
}
